/**
 * Created: 2026-10-02
 * Initial commit: Pick a course's Meituan deal from the store's deals
 *
 * Dropdown for linking a course to one of the Meituan 团购 deals of the
 * site's Meituan store. The deals are fetched through Yunfei the first time
 * the dropdown opens (this can take ~10 seconds), so the dialog does not pay
 * for the call unless the user actually wants to change the deal. A deal
 * already linked to another course is shown but cannot be picked.
 */

import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import {
  CircularProgress,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import { MeituanDealsDocument } from '../../gql/graphql'

// Created: 2026-10-02 | Initial commit: Pick a course's Meituan deal from the store's deals
gql`
  query MeituanDeals($siteId: UUID!) {
    meituanDeals(siteId: $siteId) {
      __typename
      ... on MeituanDealList {
        deals {
          dealId
          title
          price
          linkedCourseId
        }
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

interface MeituanDealSelectProps {
  siteId: string
  /** The course being edited, so its own deal is not shown as taken. */
  courseId?: string
  /** The selected deal id, or '' for no deal. */
  value: string
  onChange: (dealId: string) => void
}

export default function MeituanDealSelect({
  siteId,
  courseId,
  value,
  onChange,
}: MeituanDealSelectProps) {
  const [loadDeals, { data, loading, error, called }] = useLazyQuery(
    MeituanDealsDocument,
    { fetchPolicy: 'network-only' },
  )

  const result = data?.meituanDeals
  const deals = result?.__typename === 'MeituanDealList' ? result.deals : undefined
  const loadError =
    result?.__typename === 'GraphqlRequestError'
      ? result.message
      : error
        ? '获取美团套餐失败，请关闭后重试。'
        : undefined

  const handleOpen = () => {
    // fetch once per dialog; refetch only after a failure
    if (!called || (!loading && (loadError || !deals))) {
      loadDeals({ variables: { siteId } })
    }
  }

  const titleOf = (dealId: string) => {
    const deal = deals?.find((d) => d.dealId === dealId)
    return deal?.title ? `${deal.title}（${dealId}）` : `套餐ID ${dealId}`
  }

  return (
    <TextField
      select
      fullWidth
      label="美团套餐"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      helperText="核销该套餐的美团券码可获得本课程"
      slotProps={{
        inputLabel: { shrink: true },
        select: {
          displayEmpty: true,
          onOpen: handleOpen,
          renderValue: (selected) =>
            selected ? titleOf(selected as string) : '不关联',
        },
      }}
    >
      <MenuItem value="">不关联</MenuItem>

      {/* keeps the current deal a valid option until the list arrives */}
      {value && !deals?.some((d) => d.dealId === value) && (
        <MenuItem value={value} sx={{ display: 'none' }} />
      )}

      {loading && (
        <MenuItem disabled>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <CircularProgress size={16} />
            <span>正在获取美团套餐…</span>
          </Stack>
        </MenuItem>
      )}

      {!loading && loadError && <MenuItem disabled>{loadError}</MenuItem>}

      {!loading && deals?.length === 0 && (
        <MenuItem disabled>美团门店暂无套餐</MenuItem>
      )}

      {!loading &&
        deals?.map((deal) => {
          const takenByOther = !!deal.linkedCourseId && deal.linkedCourseId !== courseId
          return (
            <MenuItem key={deal.dealId} value={deal.dealId} disabled={takenByOther}>
              <ListItemText
                primary={deal.title ?? `套餐ID ${deal.dealId}`}
                secondary={[
                  `ID ${deal.dealId}`,
                  deal.price != null ? `¥${deal.price}` : undefined,
                  takenByOther ? '已关联其他课程' : undefined,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </MenuItem>
          )
        })}
    </TextField>
  )
}
